import React, { useState, useEffect, useRef } from 'react';

const CACHE_KEY = 'vesaro_admin_simulator_titles';
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

const AdminConfigSidebar = () => {
  const [baseConfigs, setBaseConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlug, setCurrentSlug] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasScrolledToActive, setHasScrolledToActive] = useState(false);
  const activeButtonRef = useRef(null);

  useEffect(() => {
    // Get current slug from URL
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('slug');
    setCurrentSlug(slug || '');

    // Fetch base configurations
    const fetchBaseConfigs = async () => {
      try {
        const response = await fetch('https://www.vesaro.com/_functions/test?limit=1');
        if (response.ok) {
          const data = await response.json();

          if (data.allBaseSimulators && Array.isArray(data.allBaseSimulators)) {
            // Check cache first
            const cached = localStorage.getItem(CACHE_KEY);
            let cachedTitles = {};

            if (cached) {
              try {
                const { titles, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < CACHE_DURATION) {
                  cachedTitles = titles;
                }
              } catch (e) {
                console.error('Cache parse error:', e);
              }
            }

            // Show initial list with cached titles or formatted slugs
            const initialConfigs = data.allBaseSimulators
              .filter(sim => sim.slug)
              .map(sim => ({
                slug: sim.slug,
                name: cachedTitles[sim.slug] || sim.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                id: sim.id,
                basePrice: sim.basePrice,
                hasRealTitle: !!cachedTitles[sim.slug]
              }))
              .sort((a, b) => a.name.localeCompare(b.name));

            setBaseConfigs(initialConfigs);
            setLoading(false);

            // Fetch missing titles in batches
            const needTitles = initialConfigs.filter(c => !c.hasRealTitle);

            if (needTitles.length > 0) {
              // Batch fetch with delay to avoid overwhelming the API
              const batchSize = 5;
              const delay = 100; // ms between batches

              for (let i = 0; i < needTitles.length; i += batchSize) {
                const batch = needTitles.slice(i, i + batchSize);

                await Promise.all(batch.map(async (config) => {
                  try {
                    const simResponse = await fetch(`https://www.vesaro.com/_functions/test?slug=${config.slug}`);
                    if (simResponse.ok) {
                      const simData = await simResponse.json();
                      const title = simData.baseConfig?.productName || config.name;

                      // Update cache
                      cachedTitles[config.slug] = title;

                      // Update state
                      setBaseConfigs(prev => {
                        const updated = prev.map(c =>
                          c.slug === config.slug
                            ? { ...c, name: title, hasRealTitle: true }
                            : c
                        );
                        return updated.sort((a, b) => a.name.localeCompare(b.name));
                      });
                    }
                  } catch (err) {
                    console.error(`Failed to fetch title for ${config.slug}:`, err);
                  }
                }));

                // Small delay between batches
                if (i + batchSize < needTitles.length) {
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }

              // Save to cache
              localStorage.setItem(CACHE_KEY, JSON.stringify({
                titles: cachedTitles,
                timestamp: Date.now()
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch base configs:', error);
        setLoading(false);
      }
    };

    fetchBaseConfigs();
  }, []);

  const handleConfigClick = (slug) => {
    // Navigate to the selected config with admin=true to maintain admin mode
    const newUrl = `${window.location.origin}${window.location.pathname}?slug=${slug}&admin=true`;
    window.location.href = newUrl;
  };

  const handlePriceListClick = () => {
    const newUrl = `${window.location.origin}${window.location.pathname}?slug=admin&view=prices`;
    window.location.href = newUrl;
  };

  // Filter configs based on search query
  const filteredConfigs = baseConfigs.filter(config =>
    config.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auto-scroll to active config on initial load
  useEffect(() => {
    if (!loading && !hasScrolledToActive && activeButtonRef.current) {
      const timer = setTimeout(() => {
        activeButtonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setHasScrolledToActive(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, hasScrolledToActive]);

  return (
    <div className="hidden lg:block w-64 bg-black/40 backdrop-blur-md border-r border-white/10 h-full overflow-y-auto">
      <div className="p-4">
        <div className="mb-4">
          <h2 className="text-white text-base font-semibold mb-1">Admin Panel</h2>
          <p className="text-white/60 text-xs">Base Configurations</p>
        </div>

        {/* Price List Generator Button */}
        <div className="mb-4">
          <button
            onClick={handlePriceListClick}
            className="w-full text-left px-3 py-2.5 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-500/30 transition-all duration-200"
          >
            <div className="text-sm font-medium">💰 Price List Generator</div>
          </button>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search simulators..."
              className="w-full bg-white/10 border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:bg-white/15 transition-all duration-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchQuery && !loading && (
            <div className="mt-2 text-xs text-white/50">
              Showing {filteredConfigs.length} of {baseConfigs.length}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        ) : baseConfigs.length === 0 ? (
          <div className="text-white/40 text-sm text-center py-8">
            No configurations found
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="text-white/40 text-sm text-center py-8">
            No simulators found
          </div>
        ) : (
          <div className="space-y-2">
            {filteredConfigs.map((config) => {
              const isActive = config.slug === currentSlug;

              return (
                <button
                  key={config.slug}
                  ref={isActive ? activeButtonRef : null}
                  onClick={() => handleConfigClick(config.slug)}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg
                    transition-all duration-200
                    ${isActive
                      ? 'bg-white/20 text-white border border-white/30'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-transparent'
                    }
                  `}
                >
                  <div className="text-xs font-medium leading-tight">
                    {config.name}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Admin indicator */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-green-400 text-xs">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Admin Mode Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConfigSidebar;